import { useNotification, usePermissions } from 'hooks'
import { useMemo, useState } from 'react'
import { columns, getFilters } from './helpers'
import { AddActionIcon, DeleteActionIcon, EditActionIcon, ViewActionIcon } from 'icons'
import { makeCoreGetRequest } from "utils/api"
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import s from './Locations.modules.scss'
import { Table } from 'components/core-ui/Table'
import { LocationModal } from './LocationModal'
import { DeleteLocationModal } from './DeleteLocationModal'
import { LocationSubTypeItem, LocationTableItem } from '../interfaces'
import { useNavigate } from 'react-router-dom'
import {
  getLocationRegionsOptions,
  getLocationStatusesOptions, getLocationSystemsOptions,
  getLocationTypesOptions
} from "../api"
import { AutoCompleteValue, Filter } from 'components/core-ui/Table/TableDefinitions'
import { useQuery } from 'react-query'
import { roles } from '../constants'

const Locations = () => {
  const [tableItems, setTableItems] = useState<LocationTableItem[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isNewModalOpen, setIsNewModalOpen] = useState<boolean>(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [isDisableModalOpen, setIsDisableModalOpen] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [shouldResetPage, setShouldResetPage] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] = useState<LocationTableItem>()
  const [liveTableFilters, setLiveTableFilters] = useState<Filter[]>([])

  const { showNotification } = useNotification()

  const { data: locationStatusesOptions } = getLocationStatusesOptions(showNotification)
  const { data: locationRegionsOption } = getLocationRegionsOptions(showNotification)
  const { data: locationTypesOptions } = getLocationTypesOptions(showNotification)
  const { data: locationSystemsOptions } = getLocationSystemsOptions(showNotification)

  const { data: locationSubtypesOptions } = useQuery(
    ['subtypes-filter', liveTableFilters.find(filter => filter.name === 'subtype.type.id')?.value],
    async () => {
      const typeLiveFilterValue = (liveTableFilters.find(
        filter => filter.name === 'subtype.type.id'
      )?.value || null) as AutoCompleteValue

      if (!typeLiveFilterValue?.value) return []
      const result = await makeCoreGetRequest(
        `/location-management/location-subtypes/${typeLiveFilterValue?.value}`
      )

      const resultOptions = result.map((item: LocationSubTypeItem) => ({
        label: `${item.name}${item.businessType ? ` ${item.businessType.name}` : ''}`,
        value: item.id
      }))

      return resultOptions || []
    },
    {
      onError: error => {
        showNotification({
          message: `Error while fetching subtypes data. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const filters = useMemo(
    () =>
      getFilters(
        locationStatusesOptions,
        locationTypesOptions,
        locationSubtypesOptions,
        locationRegionsOption,
        locationSystemsOptions
      ),
    [locationStatusesOptions, locationTypesOptions, locationSubtypesOptions, locationRegionsOption, locationSystemsOptions]
  )

  const navigate = useNavigate()

  const { canCreate, canEdit, canDelete } = usePermissions(roles)

  const openNewModal = (): void => setIsNewModalOpen(true)
  const closeNewModal = (): void => setIsNewModalOpen(false)

  const openEditModal = (): void => setIsEditModalOpen(true)
  const closeEditModal = (): void => setIsEditModalOpen(false)

  const openDisableModal = (): void => setIsDisableModalOpen(true)
  const closeDisableModal = (): void => setIsDisableModalOpen(false)

  const toolbarActions = useMemo(() => {
    if (canCreate) {
      return [
        {
          label: 'Add Location',
          icon: <AddActionIcon />,
          onClick: () => openNewModal()
        }
      ]
    }

    return []
  }, [canCreate])

  const selectRow = (rowId: number, action: string) => {
    const item = tableItems.find(item => item.id === rowId)
    setSelectedRow(item)
    action === 'edit' ? openEditModal() : openDisableModal()
  }

  const hasValue = (value: any) => {
    if (value == null || value === '') return false
    if (Array.isArray(value)) return value.length > 0
    return true
  }

  const normalizeExternalIdFilters = (filters: any[]) => {
    const systemFilter = filters.find(f => f.name === 'externalIdList.system.name')
    const idFilter = filters.find(f => f.name === 'externalIdList.value')

    const hasSystem = systemFilter && hasValue(systemFilter.value)
    const hasId = idFilter && hasValue(idFilter.value)

    if ((hasSystem && !hasId) || (!hasSystem && hasId)) {
      showNotification({
        message: 'Please fill both System and External ID filters, or clear them both.',
        type: 'warning'
      })

      return filters.map(f =>
        f.name === 'externalIdList.system.name' || f.name === 'externalIdList.value'
          ? { ...f, value: '' }
          : f
      )
    }

    return filters
  }

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const normalizedFilters = normalizeExternalIdFilters(tableFilters)

      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/location-management/location-usages',
          tableFilters: normalizedFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.list || []))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `Error while fetching table data. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  return (
    <div className={s.mainContainer}>
      {canCreate && (
        <LocationModal
          isModalOpen={isNewModalOpen}
          closeModal={closeNewModal}
          shouldRefreshData={shouldRefreshData}
          setShouldRefreshData={setShouldRefreshData}
        />
      )}
      {canEdit && (
        <LocationModal
          isModalOpen={isEditModalOpen}
          closeModal={closeEditModal}
          editItemId={selectedRow?.id}
          shouldRefreshData={shouldRefreshData}
          setShouldRefreshData={setShouldRefreshData}
          isEdit
        />
      )}
      {canDelete && (
        <DeleteLocationModal
          isModalOpen={isDisableModalOpen}
          closeModal={closeDisableModal}
          id={selectedRow?.id}
          setShouldRefreshData={setShouldRefreshData}
        />
      )}
      <Table
        hideCheckbox
        orderBy="code"
        order="asc"
        columns={columns}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        shouldResetPage={shouldResetPage}
        setShouldResetPage={setShouldResetPage}
        rowActions={[
          {
            label: 'Details',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#0970E6',
            desktopIcon: <ViewActionIcon />,
            onClick: (_e, rowId) => navigate(`/locationManagement/locations/${rowId}`)
          },
          canEdit && {
            label: 'Edit',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#16a085',
            desktopIcon: <EditActionIcon />,

            onClick: (_e, rowId) => selectRow(rowId, 'edit')
          },
          canDelete && {
            label: 'Delete',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#d9534f',
            desktopIcon: <DeleteActionIcon />,
            onClick: (_e, rowId) => selectRow(rowId, 'delete')
          }
        ]}
        rowActionsWidth="130px"
        toolbarActions={toolbarActions}
        filters={filters}
        handleFilterChangeLive={liveFilters => setLiveTableFilters(liveFilters)}
      />
    </div>
  )
}

export default Locations
